import type { FieldCondition, FormDataMap, FormFieldValue, FormValues } from '../types/form-builder.types';

type ConditionValue = string | number | boolean | ReadonlyArray<string | number>;

const isStringOrNumber = (value: FormFieldValue | ConditionValue | string | number): value is string | number =>
    typeof value === 'string' || typeof value === 'number';

const valueMatchesCondition = (value: FormFieldValue | undefined, expectedValue: ConditionValue): boolean => {
    if (value === undefined) {
        return false;
    }

    if (Array.isArray(value) && Array.isArray(expectedValue)) {
        return expectedValue.some((expectedItem) => value.some((selectedValue) => selectedValue === expectedItem));
    }

    if (Array.isArray(expectedValue)) {
        return isStringOrNumber(value) && expectedValue.includes(value);
    }

    if (Array.isArray(value)) {
        return isStringOrNumber(expectedValue) && value.includes(expectedValue);
    }

    return value === expectedValue;
};

const resolveFieldCondition = <FormValuesType extends FormValues, FieldId extends string>(
    condition: FieldCondition<FormValuesType, FieldId>,
    formValues: FormValuesType,
    data: FormDataMap | undefined
): boolean => {
    switch (condition.type) {
        case 'when': {
            const value = formValues[condition.field];
            return valueMatchesCondition(value, condition.value);
        }

        case 'whenNot': {
            const value = formValues[condition.field];
            return value !== undefined && !valueMatchesCondition(value, condition.value);
        }

        case 'whenHasValue': {
            const value = formValues[condition.field];
            if (typeof value === 'string') {
                return value.length > 0;
            }
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (typeof value === 'boolean') {
                return value;
            }
            if (typeof value === 'number') {
                return true;
            }
            return false;
        }

        case 'whenAll': {
            return condition.conditions.every((child) => resolveFieldCondition(child, formValues, data));
        }

        case 'whenSome': {
            return condition.conditions.some((child) => resolveFieldCondition(child, formValues, data));
        }

        case 'custom': {
            return condition.resolver(formValues);
        }

        case 'whenData': {
            if (!data) {
                return false;
            }
            return data[condition.key] === condition.value;
        }
    }
};

export const resolveCondition = <FormValuesType extends FormValues, FieldId extends string>(
    condition: boolean | FieldCondition<FormValuesType, FieldId> | undefined,
    formValues: FormValuesType,
    data?: FormDataMap
): boolean => {
    if (condition === undefined) {
        return false;
    }

    if (typeof condition === 'boolean') {
        return condition;
    }

    return resolveFieldCondition(condition, formValues, data);
};
